import { useEffect, useState } from 'react';
import { createItem, deleteItem, fetchList, fetchLookups, updateItem } from './api';
import { canWriteEmpresaData } from '../../auth/session';
import { toast } from 'react-hot-toast';
import CrudModal from '../../components/layout/CrudModal';
import { formatCurrencyBR, normalizeCurrencyInputBR, parseCurrencyBR } from '../../utils/numberBR';

const EMPTY = {
  data: '',
  competencia: '',
  tipo: 'despesa',
  classificacao_imobilizado: 'nenhum',
  descricao: '',
  valor: '',
  status: 'previsto',
  forma_pagamento: 'pix',
  conta_bancaria_id: '',
  conta_id: '',
  centro_custo_id: '',
  obra_id: '',
  pessoa_id: '',
};

const ConstrutorLancamentosPage = () => {
  const canWrite = canWriteEmpresaData();
  const [items, setItems] = useState([]);
  const [lookups, setLookups] = useState({ obras: [], contas: [], centros: [], pessoas: [], contas_bancarias: [] });
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [obraFilter, setObraFilter] = useState('');
  const [tipoFilter, setTipoFilter] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const load = async () => {
    try {
      const [list, lk] = await Promise.all([
        fetchList('/api/construtor/lancamentos', { obra_id: obraFilter, tipo: tipoFilter, q, page, pageSize: 10 }),
        fetchLookups(),
      ]);
      setItems(Array.isArray(list?.items) ? list.items : []);
      setTotal(Number(list?.total || 0));
      setLookups(lk || { obras: [], contas: [], centros: [], pessoas: [], contas_bancarias: [] });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar lancamentos');
    }
  };

  useEffect(() => {
    load();
  }, [page]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const statusFinal = String(form.status || '').toLowerCase();
    const dataFinal = String(form.data || '').trim();
    const competenciaFinal = String(form.competencia || '').trim() || (dataFinal ? dataFinal.slice(0, 7) : '');
    const descricaoFinal = String(form.descricao || '').trim();
    if (!dataFinal) {
      toast.error('Informe a data do lancamento');
      return;
    }
    if (!competenciaFinal) {
      toast.error('Informe a competencia (YYYY-MM)');
      return;
    }
    if (!form.tipo) {
      toast.error('Informe o tipo do lancamento');
      return;
    }
    if (!descricaoFinal) {
      toast.error('Informe a descricao');
      return;
    }
    if (!form.conta_id) {
      toast.error('Selecione a conta contabil');
      return;
    }
    if ((statusFinal === 'pago' || statusFinal === 'recebido') && !form.conta_bancaria_id) {
      toast.error('Selecione a conta bancaria para lancamentos pagos/recebidos');
      return;
    }
    try {
      const payload = {
        ...form,
        data: dataFinal,
        competencia: competenciaFinal,
        descricao: descricaoFinal,
        valor: parseCurrencyBR(form.valor) ?? 0,
        conta_bancaria_id: form.conta_bancaria_id || null,
        centro_custo_id: form.centro_custo_id || null,
        obra_id: form.obra_id || null,
        pessoa_id: form.pessoa_id || null,
      };
      if (editingId) {
        await updateItem('/api/construtor/lancamentos', editingId, payload);
        toast.success('Lancamento atualizado');
      } else {
        await createItem('/api/construtor/lancamentos', payload);
        toast.success('Lancamento salvo');
      }
      setEditingId('');
      setForm(EMPTY);
      setShowFormModal(false);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar lancamento');
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Excluir lancamento?')) return;
    try {
      await deleteItem('/api/construtor/lancamentos', id);
      toast.success('Lancamento excluido');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir lancamento');
    }
  };

  const onEdit = (item) => {
    setEditingId(item.id);
    setForm({
      data: String(item.data || '').slice(0, 10),
      competencia: item.competencia || '',
      tipo: item.tipo || 'despesa',
      descricao: item.descricao || '',
      valor: item.valor == null ? '' : formatCurrencyBR(item.valor),
      status: item.status || 'previsto',
      classificacao_imobilizado: item.classificacao_imobilizado || 'nenhum',
      forma_pagamento: item.forma_pagamento || 'pix',
      conta_bancaria_id: item.conta_bancaria_id || '',
      conta_id: item.conta_id || '',
      centro_custo_id: item.centro_custo_id || '',
      obra_id: item.obra_id || '',
      pessoa_id: item.pessoa_id || '',
    });
    setShowFormModal(true);
  };

  const totalPages = Math.max(1, Math.ceil(total / 10));

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Lancamentos</h1>
        {canWrite && (
          <button
            type="button"
            className="btn-primary"
            onClick={() => {
              setEditingId('');
              setForm(EMPTY);
              setShowFormModal(true);
            }}
          >
            Incluir Lancamento
          </button>
        )}
      </div>

      {canWrite && (
        <CrudModal
          open={showFormModal}
          title={editingId ? 'Editar Lancamento' : 'Novo Lancamento'}
          onClose={() => {
            setShowFormModal(false);
            setEditingId('');
            setForm(EMPTY);
          }}
          maxWidthClass="max-w-6xl"
        >
          <form onSubmit={onSubmit} className="space-y-5">
            <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
              <p className="text-sm font-semibold text-gray-800">Dados do Lancamento</p>
              <p className="text-xs text-gray-500 mt-1">Preencha os dados financeiros e classifique o movimento.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="form-label">Data</label>
                <input className="form-input" type="date" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Competencia</label>
                <input className="form-input" type="month" value={form.competencia} onChange={(e) => setForm({ ...form, competencia: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Tipo</label>
                <select className="form-select" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                  <option value="despesa">Despesa</option>
                  <option value="receita">Receita</option>
                </select>
              </div>
              <div>
                <label className="form-label">Classificacao Imobilizado</label>
                <select className="form-select" value={form.classificacao_imobilizado} onChange={(e) => setForm({ ...form, classificacao_imobilizado: e.target.value })}>
                  <option value="nenhum">Nenhum</option>
                  <option value="terreno">Terreno</option>
                  <option value="obra">Obra</option>
                  <option value="estoque">Estoque</option>
                </select>
              </div>
              <div>
                <label className="form-label">Valor (R$)</label>
                <input
                  className="form-input"
                  inputMode="decimal"
                  placeholder="0,00"
                  value={form.valor}
                  onChange={(e) => setForm({ ...form, valor: normalizeCurrencyInputBR(e.target.value) })}
                />
              </div>
              <div className="md:col-span-2">
                <label className="form-label">Descricao</label>
                <input className="form-input" placeholder="Descricao do lancamento" value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
              </div>
              <div>
                <label className="form-label">Status</label>
                <select className="form-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="previsto">Previsto</option>
                  <option value="pago">Pago</option>
                  <option value="recebido">Recebido</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>
              <div>
                <label className="form-label">Forma de Pagamento</label>
                <select className="form-select" value={form.forma_pagamento} onChange={(e) => setForm({ ...form, forma_pagamento: e.target.value })}>
                  <option value="pix">Pix</option>
                  <option value="boleto">Boleto</option>
                  <option value="cartao">Cartao</option>
                  <option value="dinheiro">Dinheiro</option>
                  <option value="transferencia">Transferencia</option>
                  <option value="outro">Outro</option>
                </select>
              </div>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-4">
              <p className="text-sm font-semibold text-gray-800">Vinculos</p>
              <p className="text-xs text-gray-500 mt-1">Associe o lancamento a contas, obra e pessoa quando necessario.</p>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
                <div>
                  <label className="form-label">Conta Bancaria</label>
                  <select className="form-select" value={form.conta_bancaria_id} onChange={(e) => setForm({ ...form, conta_bancaria_id: e.target.value })}>
                    <option value="">{(form.status === 'pago' || form.status === 'recebido') ? 'Obrigatoria' : 'Opcional'}</option>
                    {lookups.contas_bancarias.map((c) => <option key={c.id} value={c.id}>{c.banco} - {c.conta}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Conta Contabil</label>
                  <select className="form-select" value={form.conta_id} onChange={(e) => setForm({ ...form, conta_id: e.target.value })}>
                    <option value="">Selecione</option>
                    {lookups.contas.map((c) => <option key={c.id} value={c.id}>{c.codigo} - {c.descricao}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Centro de Custo</label>
                  <select className="form-select" value={form.centro_custo_id} onChange={(e) => setForm({ ...form, centro_custo_id: e.target.value })}>
                    <option value="">Opcional</option>
                    {lookups.centros.map((c) => <option key={c.id} value={c.id}>{c.codigo} - {c.descricao}</option>)}
                  </select>
                </div>
                <div>
                  <label className="form-label">Obra</label>
                  <select className="form-select" value={form.obra_id} onChange={(e) => setForm({ ...form, obra_id: e.target.value })}>
                    <option value="">Opcional</option>
                    {lookups.obras.map((o) => <option key={o.id} value={o.id}>{o.nome}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="form-label">Pessoa</label>
                  <select className="form-select" value={form.pessoa_id} onChange={(e) => setForm({ ...form, pessoa_id: e.target.value })}>
                    <option value="">Opcional</option>
                    {lookups.pessoas.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t pt-4">
              <button
                className="btn-secondary"
                type="button"
                onClick={() => {
                  setShowFormModal(false);
                  setEditingId('');
                  setForm(EMPTY);
                }}
              >
                Cancelar
              </button>
              <button className="btn-primary" type="submit">{editingId ? 'Atualizar Lancamento' : 'Salvar Lancamento'}</button>
            </div>
          </form>
        </CrudModal>
      )}

      <div className="card">
        <div className="card-header flex items-center gap-3">
          <select className="form-select max-w-sm" value={obraFilter} onChange={(e) => setObraFilter(e.target.value)}>
            <option value="">Todas obras</option>
            {lookups.obras.map((o) => <option key={o.id} value={o.id}>{o.nome}</option>)}
          </select>
          <select className="form-select max-w-xs" value={tipoFilter} onChange={(e) => setTipoFilter(e.target.value)}>
            <option value="">Todos tipos</option>
            <option value="despesa">Despesa</option>
            <option value="receita">Receita</option>
          </select>
          <input className="form-input max-w-sm" placeholder="Buscar descricao..." value={q} onChange={(e) => setQ(e.target.value)} />
          <button className="btn-secondary" onClick={() => { setPage(1); load(); }}>Filtrar</button>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Descricao</th>
                  <th>Imobilizado</th>
                  <th>Conta Bancaria</th>
                  <th>Obra</th>
                  <th>Valor</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id}>
                    <td>{String(it.data).slice(0, 10)}</td>
                    <td>{it.tipo}</td>
                    <td>{it.descricao}</td>
                    <td>{it.classificacao_imobilizado || 'nenhum'}</td>
                    <td>{it.conta_bancaria_banco ? `${it.conta_bancaria_banco} - ${it.conta_bancaria_numero}` : '-'}</td>
                    <td>{it.obra_nome || '-'}</td>
                    <td>R$ {Number(it.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td>
                      {canWrite && <button className="btn-secondary mr-2" onClick={() => onEdit(it)}>Editar</button>}
                      {canWrite && <button className="btn-danger" onClick={() => onDelete(it.id)}>Excluir</button>}
                    </td>
                  </tr>
                ))}
                {items.length === 0 && <tr><td colSpan={8} className="text-center text-gray-500 py-6">Sem dados</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-body border-t flex items-center justify-between">
          <span className="text-sm text-gray-600">Pagina {page} de {totalPages} ({total} registros)</span>
          <div className="flex gap-2">
            <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Anterior</button>
            <button className="btn-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Proxima</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConstrutorLancamentosPage;
